// Lifecycle stage for a flock card, derived from its shed context + age.
// Kept as a pure function (no store/UI) so it can be unit-tested directly and
// reused. See the F1 domain rule (use shed_type, not status) and F4-obs1
// (very old layers must read "spent", not "laying").

// Default age (weeks) at which a layer is treated as spent / past productive
// life — the standard commercial cull point. Overridable per farm via
// features.spent_layer_age_weeks.
export const SPENT_LAYER_DEFAULT_WEEKS = 72;

// Age (weeks) a layer reaches point-of-lay; below this it's still growing.
const POINT_OF_LAY_WEEKS = 18;

/**
 * resolveSpentAge turns a raw features.spent_layer_age_weeks value into the
 * effective threshold: a real value at/after point-of-lay, else the default.
 * A missing/undefined value, or an out-of-band one below point-of-lay, falls
 * back to the default. MUST stay in lockstep with the backend
 * parseSpentLayerWeeks (alert_service.go) so the "spent" badge and the
 * flock_spent cull alert agree for every stored value.
 */
export function resolveSpentAge(raw: number | null | undefined): number {
  if (typeof raw === "number" && raw >= POINT_OF_LAY_WEEKS) return raw;
  return SPENT_LAYER_DEFAULT_WEEKS;
}

export type FlockStage = "brooding" | "growing" | "laying" | "spent";

/**
 * deriveFlockStage returns the lifecycle stage for a flock.
 * - Brooder shed → always "brooding" (egg metrics hidden), regardless of age.
 * - Layer shed → "growing" (< point-of-lay), "laying" (point-of-lay … spent),
 *   or "spent" (>= spentAgeWeeks, past productive life but still active).
 */
export function deriveFlockStage(
  shedType: string | null | undefined,
  ageWeeks: number,
  spentAgeWeeks: number = SPENT_LAYER_DEFAULT_WEEKS,
): FlockStage {
  if (shedType === "brooder") return "brooding";
  if (ageWeeks >= spentAgeWeeks) return "spent";
  if (ageWeeks >= POINT_OF_LAY_WEEKS) return "laying";
  return "growing";
}
